import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import brainGameHighScores from '../../lib/brainGameHighScores';

const {
    BRAIN_GAME_HIGH_SCORE_METRICS,
    formatBrainGameHighScoreValue,
} = brainGameHighScores;

const GAME_ROUTE_PREFIX = '#/brain-games';

const MEMORY_CARDS = [
    { symbol: 'A', tone: '#35c9c1' },
    { symbol: 'B', tone: '#ff7a66' },
    { symbol: 'C', tone: '#f2c04b' },
    { symbol: 'D', tone: '#6c7cff' },
    { symbol: 'E', tone: '#22a06b' },
    { symbol: 'F', tone: '#db5bbf' },
    { symbol: 'G', tone: '#f58b37' },
    { symbol: 'H', tone: '#21a6df' },
];
const PATH_GRID_COLUMNS = 7;
const PATH_GRID_ROWS = 7;
const PATH_GRID_SIZE = PATH_GRID_COLUMNS * PATH_GRID_ROWS;
const SEQUENCE_PAD_COUNT = 9;
const N_BACK_SYMBOLS = ['K', 'R', 'S', 'T', 'V', 'Z', 'M', 'H'];
const STROOP_COLORS = [
    { id: 'red', label: 'RED', hex: '#e5484d' },
    { id: 'blue', label: 'BLUE', hex: '#2f6fed' },
    { id: 'green', label: 'GREEN', hex: '#24a06b' },
    { id: 'yellow', label: 'YELLOW', hex: '#ffd400' },
];
const COLOR_BLIND_STROOP_COLORS = [
    { id: 'blue', label: 'BLUE', hex: '#0072b2' },
    { id: 'orange', label: 'ORANGE', hex: '#e69f00' },
    { id: 'teal', label: 'TEAL', hex: '#009e73' },
    { id: 'purple', label: 'PURPLE', hex: '#cc79a7' },
];
const VISUAL_SEARCH_SYMBOLS = ['A', 'E', 'F', 'H', 'K', 'M', 'N', 'R', 'S', 'T', 'V', 'X', 'Y', 'Z'];
const FLANKER_DIRECTIONS = ['left', 'up', 'right', 'down'];
const FLANKER_ICON_BY_DIRECTION = {
    left: 'arrowLeft',
    up: 'arrowUp',
    right: 'arrowRight',
    down: 'arrowDown',
};
const CHANCE_TOTALS = [5, 6, 7, 8, 9, 10, 12, 14, 16];
const GO_NO_GO_TARGETS = ['A', 'K'];
const GO_NO_GO_DISTRACTORS = ['2', '3', '4', '5', '6', '7', '8', '9', '10', 'J', 'Q'];
const GO_NO_GO_SUITS = [
    { component: 'C', label: 'Clubs' },
    { component: 'D', label: 'Diamonds' },
    { component: 'H', label: 'Hearts' },
    { component: 'S', label: 'Spades' },
];
const GO_NO_GO_RANKS = [
    { component: 'a', label: 'Ace', rank: 'A' },
    ...GO_NO_GO_DISTRACTORS.map((rank) => ({
        component: rank === 'J' || rank === 'Q' ? rank.toLowerCase() : rank,
        label: rank === 'J' ? 'Jack' : rank === 'Q' ? 'Queen' : rank,
        rank,
    })),
    { component: 'k', label: 'King', rank: 'K' },
];
const GO_NO_GO_RESPONSE_MS = 1000;
const GO_NO_GO_FEEDBACK_MS = 650;
const BRAIN_GAME_DURATION_MS = 60000;
const TRAIL_LABELS = ['1', 'A', '2', 'B', '3', 'C', '4', 'D', '5', 'E', '6', 'F'];
const BOOTSTRAP_ICONS = {
    close: [
        { d: 'M2.146 2.854a.5.5 0 1 1 .708-.708L8 7.293l5.146-5.147a.5.5 0 0 1 .708.708L8.707 8l5.147 5.146a.5.5 0 0 1-.708.708L8 8.707l-5.146 5.147a.5.5 0 0 1-.708-.708L7.293 8z' },
    ],
    info: [
        { d: 'M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14m0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16' },
        { d: 'm8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252 1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0-.375-.193-.304-.533zM9 4.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0' },
    ],
    puzzle: [
        { d: 'M3.112 3.645A1.5 1.5 0 0 1 4.605 2H7a.5.5 0 0 1 .5.5v.382c0 .696-.497 1.182-.872 1.469a.5.5 0 0 0-.115.118l-.012.025L6.5 4.5v.003l.003.01q.005.015.036.053a.9.9 0 0 0 .27.194C7.09 4.9 7.51 5 8 5c.492 0 .912-.1 1.19-.24a.9.9 0 0 0 .271-.194.2.2 0 0 0 .036-.054l.003-.01v-.008l-.012-.025a.5.5 0 0 0-.115-.118c-.375-.287-.872-.773-.872-1.469V2.5A.5.5 0 0 1 9 2h2.395a1.5 1.5 0 0 1 1.493 1.645L12.645 6.5h.237c.195 0 .42-.147.675-.48.21-.274.528-.52.943-.52.568 0 .947.447 1.154.862C15.877 6.807 16 7.387 16 8s-.123 1.193-.346 1.638c-.207.415-.586.862-1.154.862-.415 0-.733-.246-.943-.52-.255-.333-.48-.48-.675-.48h-.237l.243 2.855A1.5 1.5 0 0 1 11.395 14H9a.5.5 0 0 1-.5-.5v-.382c0-.696.497-1.182.872-1.469a.5.5 0 0 0 .115-.118l.012-.025.001-.006v-.003l-.003-.01a.2.2 0 0 0-.036-.053.9.9 0 0 0-.27-.194C8.91 11.1 8.49 11 8 11s-.912.1-1.19.24a.9.9 0 0 0-.271.194.2.2 0 0 0-.036.054l-.003.01v.002l.001.006.012.025c.016.027.05.068.115.118.375.287.872.773.872 1.469v.382a.5.5 0 0 1-.5.5H4.605a1.5 1.5 0 0 1-1.493-1.645L3.356 9.5h-.238c-.195 0-.42.147-.675.48-.21.274-.528.52-.943.52-.568 0-.947-.447-1.154-.862C.123 9.193 0 8.613 0 8s.123-1.193.346-1.638C.553 5.947.932 5.5 1.5 5.5c.415 0 .733.246.943.52.255.333.48.48.675.48h.238z' },
    ],
    grid: [
        { d: 'M1 2a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v2a1 1 0 0 1-1 1H2a1 1 0 0 1-1-1zm5 0a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v2a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1zm5 0a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v2a1 1 0 0 1-1 1h-2a1 1 0 0 1-1-1zM1 7a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v2a1 1 0 0 1-1 1H2a1 1 0 0 1-1-1zm5 0a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v2a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1zm5 0a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v2a1 1 0 0 1-1 1h-2a1 1 0 0 1-1-1zM1 12a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v2a1 1 0 0 1-1 1H2a1 1 0 0 1-1-1zm5 0a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v2a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1zm5 0a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v2a1 1 0 0 1-1 1h-2a1 1 0 0 1-1-1z' },
    ],
    repeat: [
        { d: 'M11.534 7h3.932a.25.25 0 0 1 .192.41l-1.966 2.36a.25.25 0 0 1-.384 0l-1.966-2.36a.25.25 0 0 1 .192-.41m-11 2h3.932a.25.25 0 0 0 .192-.41L2.692 6.23a.25.25 0 0 0-.384 0L.342 8.59A.25.25 0 0 0 .534 9' },
        { d: 'M8 3c-1.552 0-2.94.707-3.857 1.818a.5.5 0 1 1-.771-.636A6.002 6.002 0 0 1 13.917 7H12.9A5 5 0 0 0 8 3M3.1 9a5.002 5.002 0 0 0 8.757 2.182.5.5 0 1 1 .771.636A6.002 6.002 0 0 1 2.083 9z', fillRule: 'evenodd' },
    ],
    layers: [
        { d: 'M7.765 1.559a.5.5 0 0 1 .47 0l7.5 4a.5.5 0 0 1 0 .882l-7.5 4a.5.5 0 0 1-.47 0l-7.5-4a.5.5 0 0 1 0-.882z' },
        { d: 'm2.125 8.567-1.86.992a.5.5 0 0 0 0 .882l7.5 4a.5.5 0 0 0 .47 0l7.5-4a.5.5 0 0 0 0-.882l-1.86-.992-5.17 2.756a1.5 1.5 0 0 1-1.41 0z' },
    ],
    palette: [
        { d: 'M12.433 10.07C14.133 10.585 16 11.15 16 8a8 8 0 1 0-8 8c1.996 0 1.826-1.504 1.649-3.08-.124-1.101-.252-2.237.351-2.92.465-.527 1.42-.237 2.433.07M8 5a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3m4.5 3a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3M5 6.5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0m.5 6.5a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3' },
    ],
    numbers: [
        { d: 'M2.873 11.297V4.142H1.699L0 5.379v1.137l1.64-1.18h.06v5.961zm3.213-5.09v-.063c0-.618.44-1.169 1.196-1.169.676 0 1.174.44 1.174 1.106 0 .624-.42 1.101-.807 1.526L4.99 10.553v.744h4.78v-.99H6.643v-.069L8.41 8.252c.65-.724 1.237-1.332 1.237-2.27C9.646 4.849 8.723 4 7.308 4c-1.573 0-2.36 1.064-2.36 2.15v.057zm6.559 1.883h.786c.823 0 1.374.481 1.379 1.179.01.707-.55 1.216-1.421 1.21-.77-.005-1.326-.419-1.379-.953h-1.095c.042 1.053.938 1.918 2.464 1.918 1.478 0 2.642-.839 2.62-2.144-.02-1.143-.922-1.651-1.551-1.714v-.063c.535-.09 1.347-.66 1.326-1.678-.026-1.053-.933-1.855-2.359-1.845-1.5.005-2.317.88-2.348 1.898h1.116c.032-.498.498-.944 1.206-.944.703 0 1.206.435 1.206 1.07.005.64-.504 1.106-1.2 1.106h-.75z' },
    ],
    pie: [
        { d: 'M15.985 8.5H8.207l-5.5 5.5A8 8 0 0 0 15.985 8.5' },
        { d: 'M2 13.292A8 8 0 0 1 7.5.015v7.778z' },
        { d: 'M8.5.015V7.5h7.485A8 8 0 0 0 8.5.015' },
    ],
    eye: [
        { d: 'M10.5 8a2.5 2.5 0 1 1-5 0 2.5 2.5 0 0 1 5 0' },
        { d: 'M0 8s3-5.5 8-5.5S16 8 16 8s-3 5.5-8 5.5S0 8 0 8m8 3.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7' },
    ],
    bullseye: [
        { d: 'M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14m0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16' },
        { d: 'M8 13A5 5 0 1 1 8 3a5 5 0 0 1 0 10m0 1A6 6 0 1 0 8 2a6 6 0 0 0 0 12' },
        { d: 'M8 11a3 3 0 1 1 0-6 3 3 0 0 1 0 6m0 1a4 4 0 1 0 0-8 4 4 0 0 0 0 8' },
        { d: 'M9.5 8a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0' },
    ],
    stop: [
        { d: 'M5 3.5h6A1.5 1.5 0 0 1 12.5 5v6a1.5 1.5 0 0 1-1.5 1.5H5A1.5 1.5 0 0 1 3.5 11V5A1.5 1.5 0 0 1 5 3.5' },
    ],
    diagram: [
        { d: 'M6 3.5A1.5 1.5 0 0 1 7.5 2h1A1.5 1.5 0 0 1 10 3.5v1A1.5 1.5 0 0 1 8.5 6v1H14a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-1 0V8h-5v.5a.5.5 0 0 1-1 0V8h-5v.5a.5.5 0 0 1-1 0v-1A.5.5 0 0 1 2 7h5.5V6A1.5 1.5 0 0 1 6 4.5zm-6 8A1.5 1.5 0 0 1 1.5 10h1A1.5 1.5 0 0 1 4 11.5v1A1.5 1.5 0 0 1 2.5 14h-1A1.5 1.5 0 0 1 0 12.5zm6 0A1.5 1.5 0 0 1 7.5 10h1a1.5 1.5 0 0 1 1.5 1.5v1A1.5 1.5 0 0 1 8.5 14h-1A1.5 1.5 0 0 1 6 12.5zm6 0a1.5 1.5 0 0 1 1.5-1.5h1a1.5 1.5 0 0 1 1.5 1.5v1a1.5 1.5 0 0 1-1.5 1.5h-1a1.5 1.5 0 0 1-1.5-1.5z', fillRule: 'evenodd' },
    ],
    check: [
        { d: 'M12.736 3.97a.733.733 0 0 1 1.047 0c.286.289.29.756.01 1.05L7.88 12.01a.733.733 0 0 1-1.065.02L3.217 8.384a.757.757 0 0 1 0-1.06.733.733 0 0 1 1.047 0l3.052 3.093 5.4-6.425z' },
    ],
    x: [
        { d: 'M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708' },
    ],
    play: [
        { d: 'm11.596 8.697-6.363 3.692c-.54.313-1.233-.066-1.233-.697V4.308c0-.63.692-1.01 1.233-.696l6.363 3.692a.802.802 0 0 1 0 1.393' },
    ],
    arrowLeft: [
        { d: 'M15 8a.5.5 0 0 0-.5-.5H2.707l3.147-3.146a.5.5 0 1 0-.708-.708l-4 4a.5.5 0 0 0 0 .708l4 4a.5.5 0 0 0 .708-.708L2.707 8.5H14.5A.5.5 0 0 0 15 8' },
    ],
    arrowUp: [
        { d: 'M8 15a.5.5 0 0 0 .5-.5V2.707l3.146 3.147a.5.5 0 0 0 .708-.708l-4-4a.5.5 0 0 0-.708 0l-4 4a.5.5 0 1 0 .708.708L7.5 2.707V14.5a.5.5 0 0 0 .5.5' },
    ],
    arrowRight: [
        { d: 'M1 8a.5.5 0 0 1 .5-.5h11.793l-3.147-3.146a.5.5 0 0 1 .708-.708l4 4a.5.5 0 0 1 0 .708l-4 4a.5.5 0 0 1-.708-.708L13.293 8.5H1.5A.5.5 0 0 1 1 8' },
    ],
    arrowDown: [
        { d: 'M8 1a.5.5 0 0 1 .5.5v11.793l3.146-3.147a.5.5 0 0 1 .708.708l-4 4a.5.5 0 0 1-.708 0l-4-4a.5.5 0 0 1 .708-.708L7.5 13.293V1.5A.5.5 0 0 1 8 1' },
    ],
    gem: [
        { d: 'M3.1.7a.5.5 0 0 1 .4-.2h9a.5.5 0 0 1 .4.2l2.976 3.974c.149.185.156.45.01.644L8.4 15.3a.5.5 0 0 1-.8 0L.1 5.3a.5.5 0 0 1 0-.6zm11.386 3.785-1.806-2.41-.776 2.413zm-3.633.004.961-2.989H4.186l.963 2.995zM5.47 5.495 8 13.366l2.532-7.876zm-1.371-.999-.78-2.422-1.818 2.425zM1.499 5.5l5.113 6.817-2.192-6.82zm7.889 6.817 5.123-6.83-2.928.002z' },
    ],
};

const BRAIN_GAME_ICON_BY_ID = {
    'memory-match': 'puzzle',
    'memory-matrix': 'grid',
    'sequence-repeat': 'repeat',
    'symbol-n-back': 'layers',
    'stroop-colors': 'palette',
    'schulte-table': 'numbers',
    'visual-search': 'eye',
    'flanker-direction': 'eye',
    'go-no-go': 'stop',
    'trail-making': 'diagram',
    'chance-cards': 'numbers',
    'turn-match': 'repeat',
    'number-rain': 'numbers',
    'order-rush': 'puzzle',
    'estimate-duel': 'bullseye',
    'new-catch': 'eye',
    'dual-track': 'diagram',
};

const BRAIN_GAME_ICON_IMAGE_BY_ID = {
    'memory-match': '/assets/ufov/brain-games/icons/memory-match.png',
    'memory-matrix': '/assets/ufov/brain-games/icons/path-memory.png',
    'sequence-repeat': '/assets/ufov/brain-games/icons/sequence-repeat.png',
    'symbol-n-back': '/assets/ufov/brain-games/icons/symbol-n-back.png',
    'stroop-colors': '/assets/ufov/brain-games/icons/stroop-colors.png',
    'schulte-table': '/assets/ufov/brain-games/icons/schulte-table.png',
    'visual-search': '/assets/ufov/brain-games/icons/sopt-classic.png',
    'flanker-direction': '/assets/ufov/brain-games/icons/flanker-direction.png',
    'go-no-go': '/assets/ufov/brain-games/icons/go-no-go.png',
    'trail-making': '/assets/ufov/brain-games/icons/trail-making.png',
    'chance-cards': '/assets/ufov/brain-games/icons/number-odds.png',
    'turn-match': '/assets/ufov/brain-games/icons/turn-match.png',
    'number-rain': '/assets/ufov/brain-games/icons/number-rain.png',
    'order-rush': '/assets/ufov/brain-games/icons/order-rush.png',
    'estimate-duel': '/assets/ufov/brain-games/icons/estimate-duel.png',
    'new-catch': '/assets/ufov/brain-games/icons/new-catch.png',
    'dual-track': '/assets/ufov/brain-games/icons/trail-making.png',
};

const BRAIN_GAME_TUTORIALS = {
    'memory-match': ['Flip two tiles.', 'Match the same image.', 'Clear the board.'],
    'memory-matrix': ['Watch the countdown.', 'Remember the blue tiles.', 'Tap them after they flip.'],
    'sequence-repeat': ['Watch the flashes.', 'Repeat the same pads.', 'The chain grows after each success.'],
    'symbol-n-back': ['Look at the current symbol.', 'Compare it with two turns ago.', 'Tap yes or no.'],
    'stroop-colors': ['Read the word.', 'Ignore the ink color.', 'Tap the matching color.'],
    'schulte-table': ['Find 1 first.', 'Keep going upward.', 'Finish at 25.'],
    'visual-search': ['Memorize the target image.', 'Find it in the grid.', 'Next target appears automatically.'],
    'flanker-direction': ['Look at the middle arrow.', 'Ignore the side arrows.', 'Tap its direction.'],
    'go-no-go': ['Watch each card.', 'Press only for an Ace or King.', 'Do nothing for every other card.'],
    'trail-making': ['Start at 1.', 'Alternate number, letter.', 'Follow the path without misses.'],
    'chance-cards': ['Compare both odds.', 'Pick the larger chance.', 'Answer before it changes.'],
    'turn-match': ['Study the cube shape.', 'Find the same shape turned around.', 'Reflections and changed cubes do not match.'],
    'number-rain': ['Solve the equation.', 'Tap the correct answer.', 'Keep the streak alive.'],
    'order-rush': ['Study the order.', 'Wait for the cards to turn.', 'Rebuild it from left to right.'],
    'estimate-duel': ['Compare both groups.', 'Tap the side with more dots.', 'Trust the quick estimate.'],
    'new-catch': ['Watch the board.', 'One tile changes.', 'Tap the changed tile.'],
};

const createAssetItems = (scenarioId, prefix, count) => (
    Array.from({ length: count }, (_, index) => {
        const symbolId = `${prefix}-${String(index + 1).padStart(2, '0')}`;
        return {
            id: symbolId,
            src: `/assets/ufov/scenarios/${scenarioId}/symbols/${symbolId}.png`,
        };
    })
);

const BRAIN_ASSET_SETS = [
    { id: 'dogs', tone: '#ff8ab3', items: createAssetItems('dog-breed-board', 'dog', 20) },
    { id: 'flowers', tone: '#b874ff', items: createAssetItems('flower-garden', 'flower', 16) },
    { id: 'keys', tone: '#4dc4ff', items: createAssetItems('key-hook-board', 'key', 20) },
    { id: 'rocks', tone: '#ffb347', items: createAssetItems('rock-carrier-box', 'rock', 24) },
    { id: 'screws', tone: '#6fe7b7', items: createAssetItems('screws-box', 'screw', 16) },
    { id: 'shells', tone: '#ff7fdb', items: createAssetItems('shell-tray', 'shell', 10) },
];

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
const percent = (value, total) => `${clamp(total ? (value / total) * 100 : 0, 0, 100)}%`;

function shuffle(items) {
    const nextItems = [...items];

    for (let index = nextItems.length - 1; index > 0; index -= 1) {
        const swapIndex = Math.floor(Math.random() * (index + 1));
        [nextItems[index], nextItems[swapIndex]] = [nextItems[swapIndex], nextItems[index]];
    }

    return nextItems;
}

function randomItem(items) {
    return items[Math.floor(Math.random() * items.length)];
}

function formatElapsed(ms) {
    return `${(Math.max(0, ms) / 1000).toFixed(1)}s`;
}

function formatGameSeconds(ms) {
    return `${Math.max(0, Math.ceil(ms / 1000))}`;
}

function useTicker(active) {
    const [now, setNow] = useState(Date.now());

    useEffect(() => {
        if (!active) {
            return undefined;
        }

        const interval = window.setInterval(() => setNow(Date.now()), 120);
        return () => window.clearInterval(interval);
    }, [active]);

    return now;
}

function usePausableTimeout(paused = false) {
    const timeoutRef = useRef(null);
    const callbackRef = useRef(null);
    const deadlineRef = useRef(0);
    const remainingRef = useRef(0);
    const pausedRef = useRef(Boolean(paused));

    const cancelScheduledTimeout = useCallback(() => {
        if (timeoutRef.current !== null) {
            window.clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
    }, []);

    const clear = useCallback(() => {
        cancelScheduledTimeout();
        callbackRef.current = null;
        deadlineRef.current = 0;
        remainingRef.current = 0;
    }, [cancelScheduledTimeout]);

    const arm = useCallback((delayMs) => {
        if (typeof callbackRef.current !== 'function') {
            return;
        }

        const delay = Math.max(0, Number(delayMs) || 0);
        remainingRef.current = delay;
        deadlineRef.current = Date.now() + delay;
        timeoutRef.current = window.setTimeout(() => {
            timeoutRef.current = null;
            deadlineRef.current = 0;
            remainingRef.current = 0;
            const callback = callbackRef.current;
            callbackRef.current = null;
            callback?.();
        }, delay);
    }, []);

    const schedule = useCallback((callback, delayMs) => {
        clear();
        callbackRef.current = callback;
        remainingRef.current = Math.max(0, Number(delayMs) || 0);
        if (!pausedRef.current) {
            arm(remainingRef.current);
        }
    }, [arm, clear]);

    useEffect(() => {
        const nextPaused = Boolean(paused);
        const wasPaused = pausedRef.current;
        pausedRef.current = nextPaused;

        if (nextPaused && !wasPaused && timeoutRef.current !== null) {
            remainingRef.current = Math.max(0, deadlineRef.current - Date.now());
            cancelScheduledTimeout();
            return;
        }

        if (!nextPaused && wasPaused && typeof callbackRef.current === 'function') {
            arm(remainingRef.current);
        }
    }, [arm, cancelScheduledTimeout, paused]);

    useEffect(() => clear, [clear]);

    return { clear, schedule };
}

const FOCUSABLE_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
].join(',');

function useDialogFocusTrap(dialogRef, restoreFocusRef = null, fallbackFocusSelector = '') {
    useEffect(() => {
        const dialog = dialogRef.current;
        const previouslyFocused = restoreFocusRef?.current || document.activeElement;
        if (!dialog) {
            return undefined;
        }

        const getFocusableElements = () => Array.from(dialog.querySelectorAll(FOCUSABLE_SELECTOR))
            .filter((element) => !element.hasAttribute('hidden') && element.getAttribute('aria-hidden') !== 'true');
        const initialFocus = dialog.querySelector('[autofocus]') || getFocusableElements()[0] || dialog;
        initialFocus.focus({ preventScroll: true });

        const handleKeyDown = (event) => {
            if (event.key !== 'Tab') {
                return;
            }

            const focusableElements = getFocusableElements();
            if (!focusableElements.length) {
                event.preventDefault();
                dialog.focus({ preventScroll: true });
                return;
            }

            const firstFocusable = focusableElements[0];
            const lastFocusable = focusableElements[focusableElements.length - 1];
            if (event.shiftKey && document.activeElement === firstFocusable) {
                event.preventDefault();
                lastFocusable.focus();
            } else if (!event.shiftKey && document.activeElement === lastFocusable) {
                event.preventDefault();
                firstFocusable.focus();
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            window.requestAnimationFrame(() => {
                const isPageRoot = previouslyFocused === document.body || previouslyFocused === document.documentElement;
                if (!isPageRoot && previouslyFocused?.isConnected && typeof previouslyFocused.focus === 'function') {
                    previouslyFocused.focus({ preventScroll: true });
                    return;
                }

                document.querySelector(fallbackFocusSelector)?.focus?.({ preventScroll: true });
            });
        };
    }, [dialogRef, fallbackFocusSelector, restoreFocusRef]);
}

function getNextAssetSet(excludeSetId = '') {
    const candidates = BRAIN_ASSET_SETS.filter((set) => set.id !== excludeSetId);
    return randomItem(candidates.length ? candidates : BRAIN_ASSET_SETS);
}

function createMemoryState(excludeSetId = '', pairCount = 8, preferredSetId = '') {
    const assetSet = BRAIN_ASSET_SETS.find((set) => set.id === preferredSetId)
        || getNextAssetSet(excludeSetId);
    const safePairCount = Math.max(1, Math.min(8, Math.round(Number(pairCount) || 8)));
    const cards = shuffle(assetSet.items).slice(0, safePairCount);

    return {
        assetSet,
        deck: shuffle(cards.flatMap((card, pairIndex) => [
            { id: `${card.id}-${pairIndex}-a`, matchId: card.id, src: card.src, tone: assetSet.tone },
            { id: `${card.id}-${pairIndex}-b`, matchId: card.id, src: card.src, tone: assetSet.tone },
        ])),
    };
}

function createMemoryDeck() {
    return shuffle(MEMORY_CARDS.flatMap((card, pairIndex) => [
        { id: `${card.symbol}-${pairIndex}-a`, matchId: card.symbol, ...card },
        { id: `${card.symbol}-${pairIndex}-b`, matchId: card.symbol, ...card },
    ]));
}

function getPathNeighbors(cellIndex) {
    const row = Math.floor(cellIndex / PATH_GRID_COLUMNS);
    const column = cellIndex % PATH_GRID_COLUMNS;
    const neighbors = [];

    if (row > 0) {
        neighbors.push(cellIndex - PATH_GRID_COLUMNS);
    }

    if (row < PATH_GRID_ROWS - 1) {
        neighbors.push(cellIndex + PATH_GRID_COLUMNS);
    }

    if (column > 0) {
        neighbors.push(cellIndex - 1);
    }

    if (column < PATH_GRID_COLUMNS - 1) {
        neighbors.push(cellIndex + 1);
    }

    return neighbors;
}

function createPathMemoryRound(level = 1) {
    const targetLength = clamp(6 + Math.floor(level / 2), 6, 14);

    for (let attempt = 0; attempt < 48; attempt += 1) {
        const path = [Math.floor(Math.random() * PATH_GRID_SIZE)];

        while (path.length < targetLength) {
            const visited = new Set(path);
            const candidates = getPathNeighbors(path[path.length - 1]).filter((cell) => !visited.has(cell));

            if (!candidates.length) {
                break;
            }

            path.push(randomItem(candidates));
        }

        if (path.length === targetLength) {
            return {
                id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
                path,
            };
        }
    }

    return {
        id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
        path: Array.from({ length: targetLength }, (_, index) => index * PATH_GRID_COLUMNS + Math.floor(PATH_GRID_COLUMNS / 2)),
    };
}

function createTrailNodes(pairCount = 6) {
    const safePairCount = clamp(Math.round(Number(pairCount) || 6), 1, 6);

    return shuffle(TRAIL_LABELS.slice(0, safePairCount * 2).map((label, order) => ({
        label,
        order,
    })));
}

function createNBackSequence(length = 24, n = 2) {
    const sequence = [];

    for (let index = 0; index < length; index += 1) {
        const shouldMatch = index >= n && Math.random() < 0.34;
        if (shouldMatch) {
            sequence.push(sequence[index - n]);
            continue;
        }

        const blocked = index >= n ? sequence[index - n] : '';
        const options = N_BACK_SYMBOLS.filter((symbol) => symbol !== blocked);
        sequence.push(randomItem(options));
    }

    return sequence;
}

function createStroopRound(colors = STROOP_COLORS) {
    const word = randomItem(colors);
    let ink = randomItem(colors);

    if (Math.random() < 0.76) {
        ink = randomItem(colors.filter((color) => color.id !== word.id));
    }

    return { word, ink };
}

function createVisualSearchRound(round, cellCount = 30) {
    const assetSet = BRAIN_ASSET_SETS[(round + 1) % BRAIN_ASSET_SETS.length];
    const options = shuffle(assetSet.items).slice(0, 6);
    const target = randomItem(options);
    const distractors = options.filter((item) => item.id !== target.id);
    const safeCellCount = clamp(Math.round(Number(cellCount) || 30), 6, 30);
    const targetIndex = Math.floor(Math.random() * safeCellCount);
    const cells = Array.from({ length: safeCellCount }, (_, index) => {
        const item = index === targetIndex ? target : randomItem(distractors);

        return {
            id: `${round}-${index}`,
            item,
            target: index === targetIndex,
            rotate: index !== targetIndex && index % 4 === 0,
            small: index % 7 === 0,
            muted: index % 5 === 0,
        };
    });

    return {
        assetSet,
        target,
        cells: shuffle(cells).map((cell, index) => ({ ...cell, id: `${round}-${index}` })),
    };
}

function createFlankerRound(round) {
    const target = randomItem(FLANKER_DIRECTIONS);
    const oppositeByDirection = {
        left: 'right',
        right: 'left',
        up: 'down',
        down: 'up',
    };
    const distractorOptions = FLANKER_DIRECTIONS.filter((direction) => direction !== target);
    const opposite = oppositeByDirection[target] || randomItem(distractorOptions);
    const congruentChance = clamp(0.56 - round * 0.025, 0.22, 0.56);
    const distractor = Math.random() < congruentChance
        ? target
        : (Math.random() < 0.72 ? opposite : randomItem(distractorOptions));
    const outerNoise = round > 7 && Math.random() < 0.34 ? randomItem(distractorOptions) : distractor;

    return {
        id: `${round}-${Date.now()}`,
        target,
        arrows: [outerNoise, distractor, target, distractor, outerNoise],
    };
}

function createChanceOption(total, wins) {
    return {
        total,
        wins,
        ratio: wins / total,
    };
}

function createChanceRound(round) {
    const maxIndex = clamp(3 + Math.floor(round / 2), 3, CHANCE_TOTALS.length - 1);
    const minGap = round > 8 ? 0.035 : round > 4 ? 0.055 : 0.085;

    for (let attempt = 0; attempt < 80; attempt += 1) {
        const leftTotal = randomItem(CHANCE_TOTALS.slice(0, maxIndex + 1));
        const rightTotal = randomItem(CHANCE_TOTALS.slice(0, maxIndex + 1));
        const leftWins = clamp(1 + Math.floor(Math.random() * (leftTotal - 1)), 1, leftTotal - 1);
        const rightWins = clamp(1 + Math.floor(Math.random() * (rightTotal - 1)), 1, rightTotal - 1);
        const left = createChanceOption(leftTotal, leftWins);
        const right = createChanceOption(rightTotal, rightWins);
        const gap = Math.abs(left.ratio - right.ratio);

        if (gap >= minGap) {
            return {
                id: `${round}-${Date.now()}`,
                left,
                right,
                correctSide: left.ratio > right.ratio ? 'left' : 'right',
            };
        }
    }

    const left = createChanceOption(8, 5);
    const right = createChanceOption(10, 5);

    return {
        id: `${round}-${Date.now()}`,
        left,
        right,
        correctSide: 'left',
    };
}

function createGoNoGoStimuli() {
    const deck = GO_NO_GO_SUITS.flatMap((suit) => GO_NO_GO_RANKS.map((rank) => ({
        component: `${suit.component}${rank.component}`,
        go: GO_NO_GO_TARGETS.includes(rank.rank),
        label: `${rank.label} of ${suit.label}`,
        rank: rank.rank,
        suit: suit.label,
    })));

    return shuffle(deck).map((card, index) => ({
        ...card,
        id: `${index}-${card.component}`,
    }));
}

function createTurnMatchRound(round = 1, excludeSetId = '') {
    const assetSet = getNextAssetSet(excludeSetId);
    const targetItem = randomItem(assetSet.items);
    const decoys = shuffle(assetSet.items.filter((item) => item.id !== targetItem.id)).slice(0, 2);
    const rotations = round > 7 ? [45, 90, 135, 180, 225, 270, 315] : [90, 180, 270];
    const rotation = randomItem(rotations);

    return {
        id: `${round}-${Date.now()}`,
        assetSet,
        source: targetItem,
        correctId: 'match',
        options: shuffle([
            {
                id: 'match',
                item: targetItem,
                mirrored: false,
                rotation,
            },
            {
                id: 'mirror',
                item: targetItem,
                mirrored: true,
                rotation,
            },
            {
                id: 'decoy-a',
                item: decoys[0] || targetItem,
                mirrored: false,
                rotation: randomItem(rotations),
            },
            {
                id: 'decoy-b',
                item: decoys[1] || targetItem,
                mirrored: true,
                rotation: randomItem(rotations),
            },
        ]),
    };
}

function createNumberRainRound(round = 1) {
    const maxNumber = clamp(7 + Math.floor(round / 2), 7, 14);
    const operator = round > 5 ? randomItem(['+', '-', 'x']) : randomItem(['+', '-']);
    let left = 1 + Math.floor(Math.random() * maxNumber);
    let right = 1 + Math.floor(Math.random() * maxNumber);
    let answer = left + right;

    if (operator === '-') {
        if (right > left) {
            [left, right] = [right, left];
        }
        answer = left - right;
    } else if (operator === 'x') {
        left = 2 + Math.floor(Math.random() * clamp(2 + Math.floor(round / 2), 3, 9));
        right = 2 + Math.floor(Math.random() * clamp(2 + Math.floor(round / 3), 3, 9));
        answer = left * right;
    }

    const optionSet = new Set([answer]);
    const spread = operator === 'x' ? 18 : 9;

    while (optionSet.size < 4) {
        const delta = Math.floor(Math.random() * spread) - Math.floor(spread / 2);
        const option = Math.max(0, answer + (delta || optionSet.size));
        optionSet.add(option);
    }

    return {
        id: `${round}-${Date.now()}`,
        equation: `${left} ${operator} ${right}`,
        answer,
        options: shuffle([...optionSet]),
    };
}

function createOrderRushRound(round = 1, excludeSetId = '') {
    const assetSet = getNextAssetSet(excludeSetId);
    const orderLength = clamp(2 + Math.floor(round / 4), 2, 4);
    const order = shuffle(assetSet.items).slice(0, orderLength);
    const distractors = shuffle(assetSet.items.filter((item) => !order.some((orderItem) => orderItem.id === item.id)))
        .slice(0, Math.max(0, 6 - order.length));

    return {
        id: `${round}-${Date.now()}`,
        assetSet,
        order,
        tray: shuffle([...order, ...distractors]),
    };
}

function createEstimateDots(count) {
    return Array.from({ length: count }, (_, index) => ({
        id: index,
        x: 10 + Math.random() * 80,
        y: 11 + Math.random() * 78,
        size: 7 + Math.random() * 7,
        opacity: 0.7 + Math.random() * 0.24,
    }));
}

function createEstimateDuelRound(round = 1) {
    const maxLower = clamp(13 + Math.floor(round / 2), 13, 25);
    const lowerCount = 8 + Math.floor(Math.random() * Math.max(1, maxLower - 7));
    const gap = (round > 12 ? 2 : round > 6 ? 3 : 5) + Math.floor(Math.random() * 4);
    const higherCount = lowerCount + gap;
    const higherSide = Math.random() < 0.5 ? 'left' : 'right';
    const leftCount = higherSide === 'left' ? higherCount : lowerCount;
    const rightCount = higherSide === 'right' ? higherCount : lowerCount;

    return {
        id: `${round}-${Date.now()}`,
        correctSide: higherSide,
        left: {
            dots: createEstimateDots(leftCount),
        },
        right: {
            dots: createEstimateDots(rightCount),
        },
    };
}

function createNewCatchRound(round = 1, excludeSetId = '') {
    const assetSet = getNextAssetSet(excludeSetId);
    const cellCount = clamp(4 + Math.floor((round - 1) / 5), 4, 6);
    const cells = shuffle(assetSet.items).slice(0, cellCount).map((item, index) => ({
        id: `${round}-${index}-${item.id}`,
        item,
    }));
    const targetIndex = Math.floor(Math.random() * cells.length);
    const usedIds = new Set(cells.map((cell) => cell.item.id));
    const replacements = assetSet.items.filter((item) => !usedIds.has(item.id) && item.id !== cells[targetIndex].item.id);
    const fallbackReplacements = assetSet.items.filter((item) => item.id !== cells[targetIndex].item.id);

    return {
        id: `${round}-${Date.now()}`,
        assetSet,
        cells,
        replacement: randomItem(replacements.length ? replacements : fallbackReplacements),
        targetIndex,
    };
}

function BrainTrainingGameIcon({ icon, className = '' }) {
    const paths = Array.isArray(icon) ? icon : BOOTSTRAP_ICONS[icon] || BOOTSTRAP_ICONS.puzzle;

    return (
        <svg aria-hidden="true" className={className} viewBox="0 0 16 16">
            {paths.map((path, index) => (
                <path
                    d={path.d}
                    fill="currentColor"
                    fillRule={path.fillRule}
                    key={`${path.d}-${index}`}
                />
            ))}
        </svg>
    );
}

function BrainGameHeader({
    activeGame,
    copy = {},
    metrics = {},
    onClose,
    onInfo,
    actionsEnabled = true,
    infoButtonRef = null,
    timeRemainingMs = null,
}) {
    const headerIcon = activeGame?.icon || BRAIN_GAME_ICON_BY_ID[activeGame.id];
    const level = Number.isFinite(metrics.level) && metrics.level > 0
        ? Math.round(metrics.level)
        : null;
    const remainingSeconds = Number.isFinite(timeRemainingMs)
        ? Math.max(0, Math.ceil(timeRemainingMs / 1000))
        : null;
    const visibleTime = remainingSeconds === null
        ? null
        : `${Math.floor(remainingSeconds / 60)}:${String(remainingSeconds % 60).padStart(2, '0')}`;
    const difficultyLabel = copy.currentDifficulty || copy.level || 'Difficulty';

    return (
        <header className="UFOV-brain-games__top">
            <div className="UFOV-brain-games__top-mark" aria-hidden="true">
                <BrainTrainingGameIcon icon={headerIcon} />
            </div>
            <div className="UFOV-brain-games__top-spacer">
                {level ? (
                    <div className="UFOV-brain-games__level" aria-label={`${difficultyLabel} ${level}`} role="status">
                        <span>LV</span>
                        <strong>{level}</strong>
                    </div>
                ) : null}
                {visibleTime !== null ? (
                    <div
                        aria-label={`${copy.timeRemaining || 'Time remaining'}: ${remainingSeconds} ${copy.seconds || 'seconds'}`}
                        className="UFOV-brain-games__run-timer"
                        role="timer"
                    >
                        <span aria-hidden="true">{visibleTime}</span>
                    </div>
                ) : null}
            </div>
            <div className="UFOV-brain-games__top-actions">
                <button
                    aria-label={copy.howToPlay || 'How to play'}
                    className="UFOV-brain-games__icon-button"
                    disabled={!actionsEnabled}
                    onClick={onInfo}
                    ref={infoButtonRef}
                    type="button"
                >
                    <BrainTrainingGameIcon icon="info" />
                </button>
                <button className="UFOV-brain-games__icon-button" type="button" onClick={onClose} aria-label={copy.closeGames || 'Close games'}>
                    <BrainTrainingGameIcon icon="close" />
                </button>
            </div>
        </header>
    );
}

function TutorialModal({ copy = {}, game, initialLevel = null, onClose, restoreFocusRef = null }) {
    const dialogRef = useRef(null);
    const baseRules = game.tutorial || BRAIN_GAME_TUTORIALS[game.id] || [];
    const level = Number.isFinite(Number(initialLevel)) ? Math.round(Number(initialLevel)) : null;
    let rules = baseRules;

    if (game.id === 'symbol-n-back' && level && baseRules.length > 1) {
        const n = clamp(level, 1, 3);
        rules = [
            baseRules[0],
            `Compare it with ${n === 1 ? 'one turn' : `${n} turns`} ago.`,
            baseRules[baseRules.length - 1],
        ];
    } else if (game.id === 'schulte-table' && level && baseRules.length > 1) {
        const sideLength = clamp(level, 1, 3) + 2;
        rules = [
            baseRules[0],
            baseRules[1],
            `Finish at ${sideLength * sideLength}.`,
        ];
    }

    const tutorialIcon = game.icon || BRAIN_GAME_ICON_BY_ID[game.id];
    useDialogFocusTrap(dialogRef, restoreFocusRef, '.UFOV-brain-games__top-actions .UFOV-brain-games__icon-button:not([disabled])');

    return (
        <div className="UFOV-brain-games__tutorial-backdrop" role="presentation">
            <div
                aria-label={`${game.title} ${copy.rulesLabel || 'rules'}`}
                aria-modal="true"
                className="UFOV-brain-games__tutorial"
                ref={dialogRef}
                role="dialog"
                tabIndex="-1"
            >
                <div className="UFOV-brain-games__tutorial-icon" aria-hidden="true">
                    <BrainTrainingGameIcon icon={tutorialIcon} />
                </div>
                <strong>{game.title}</strong>
                <ol>
                    {rules.map((rule) => <li key={rule}>{rule}</li>)}
                </ol>
                <button
                    type="button"
                    onClick={onClose}
                    autoFocus
                >
                    {copy.start || 'Start'}
                </button>
            </div>
        </div>
    );
}

const GameCloseContext = React.createContext(null);
const GameResultContext = React.createContext(null);
const BrainGameHighScoreContext = React.createContext(null);

function GameShell({
    children,
    tone = '',
    disabled = false,
    game = null,
    copy = {},
    hud = {},
    metrics = null,
    onClose = null,
    onMetricsChange,
    onRunComplete = null,
    onRunRestart = null,
}) {
    const metricPayload = metrics || {};
    const metricKey = JSON.stringify(metricPayload);
    const startLevelRef = useRef(null);
    const bestStreakRef = useRef(0);
    const currentLevel = Number.isFinite(metricPayload.level) && metricPayload.level > 0
        ? Math.round(metricPayload.level)
        : null;

    if (startLevelRef.current === null && currentLevel !== null) {
        startLevelRef.current = currentLevel;
    }

    if (Number.isFinite(metricPayload.streak)) {
        bestStreakRef.current = Math.max(bestStreakRef.current, metricPayload.streak);
    }

    const resetResultRun = useCallback(() => {
        startLevelRef.current = null;
        bestStreakRef.current = 0;
        onRunRestart?.();
    }, [onRunRestart]);

    const resultContext = useMemo(() => ({
        bestStreak: bestStreakRef.current,
        copy,
        game,
        hud,
        levelStart: startLevelRef.current,
        metrics: metricPayload,
        reportRunComplete: onRunComplete,
        resetResultRun,
    }), [copy, game, hud, metricKey, onRunComplete, resetResultRun]);

    useEffect(() => {
        if (typeof onMetricsChange === 'function') {
            onMetricsChange(metricPayload);
        }
    }, [metricKey, onMetricsChange]);

    return (
        <GameCloseContext.Provider value={onClose}>
            <GameResultContext.Provider value={resultContext}>
                <article className={`UFOV-brain-games__play ${tone ? `is-${tone}` : ''} ${disabled ? 'is-disabled' : ''}`.trim()}>
                    <div className="UFOV-brain-games__play-body">{children}</div>
                </article>
            </GameResultContext.Provider>
        </GameCloseContext.Provider>
    );
}

function GameButton({ children, className = '', variant = 'primary', icon = null, ...buttonProps }) {
    return (
        <button className={`UFOV-brain-games__button is-${variant} ${className}`.trim()} type="button" {...buttonProps}>
            {icon ? <BrainTrainingGameIcon icon={icon} /> : children}
        </button>
    );
}

function ResultMetrics({ copy = {}, metrics = {}, hud = {} }) {
    const hasConfiguredHud = Object.keys(hud).length > 0;
    const shows = (key, fallback) => hasConfiguredHud ? hud[key] === true : fallback;
    const items = [
        shows('score', Number.isFinite(metrics.score)) ? {
            key: 'score',
            icon: 'bullseye',
            label: copy.score || 'Score',
            value: Number.isFinite(metrics.maxScore)
                ? `${metrics.score || 0}/${metrics.maxScore}`
                : `${metrics.score || 0}`,
        } : null,
        shows('streak', Number.isFinite(metrics.streak)) ? {
            key: 'streak',
            icon: 'repeat',
            label: copy.bestStreak || 'Best streak',
            value: `${metrics.streak || 0}`,
        } : null,
        shows('mistakes', Number.isFinite(metrics.mistakes)) ? {
            key: 'mistakes',
            icon: 'x',
            label: copy.mistakes || 'Mistakes',
            value: `${metrics.mistakes || 0}`,
        } : null,
        shows('elapsed', Number.isFinite(metrics.elapsedMs)) ? {
            key: 'elapsed',
            icon: 'numbers',
            label: copy.time || 'Time',
            value: formatElapsed(metrics.elapsedMs || 0),
        } : null,
        Number.isFinite(metrics.durationMs) ? {
            key: 'duration',
            icon: 'numbers',
            label: copy.duration || 'Duration',
            value: `${Math.round(metrics.durationMs / 1000)} ${copy.seconds || 'seconds'}`,
        } : null,
    ].filter(Boolean);

    if (!items.length) {
        return null;
    }

    return (
        <div className="UFOV-brain-games__result-metrics" aria-label={copy.runResult || 'Run result'}>
            {items.map((item) => (
                <span aria-label={`${item.label} ${item.value}`} className={`is-${item.key}`} key={item.key}>
                    <i aria-hidden="true"><BrainTrainingGameIcon icon={item.icon} /></i>
                    <small>{item.label}</small>
                    <b>{item.value}</b>
                </span>
            ))}
        </div>
    );
}

function ResultLevelChange({ copy = {}, level, levelStart }) {
    if (!Number.isFinite(level) || !Number.isFinite(levelStart)) {
        return null;
    }

    const delta = level - levelStart;
    const state = delta > 0 ? 'up' : delta < 0 ? 'down' : 'same';
    const symbol = delta > 0 ? '\u2191' : delta < 0 ? '\u2193' : '\u2192';
    const amount = `${delta > 0 ? '+' : ''}${delta}`;
    const changeLabel = copy.difficultyChange || copy.levelChange || 'Difficulty change';

    return (
        <div className={`UFOV-brain-games__result-change is-${state}`} aria-label={`${changeLabel} ${amount}`}>
            <span aria-hidden="true">{symbol}</span>
            <strong>{amount}</strong>
            <small>{changeLabel}</small>
        </div>
    );
}

function PersonalBestResult({ copy = {}, highScoreResult = null }) {
    const best = highScoreResult?.best;

    if (!best) {
        return null;
    }

    const label = best.metric === BRAIN_GAME_HIGH_SCORE_METRICS.SCORE
        ? copy.highScore || 'High score'
        : best.metric === BRAIN_GAME_HIGH_SCORE_METRICS.STREAK
            ? copy.bestStreak || 'Best streak'
            : best.metric === BRAIN_GAME_HIGH_SCORE_METRICS.ELAPSED_MS
                ? copy.bestTime || 'Best time'
                : copy.highestLevel || 'Highest level';

    return (
        <div className={`UFOV-brain-games__personal-best ${highScoreResult.isNewHighScore ? 'is-new' : ''}`.trim()}>
            <div>
                <small>{label}</small>
                <strong>{formatBrainGameHighScoreValue(best)}</strong>
            </div>
            {highScoreResult.isNewHighScore ? (
                <span role="status">{copy.newHighScore || 'New high score'}</span>
            ) : null}
        </div>
    );
}

function GameResultOverlay({ className = '', copy = null, game = null, hud = null, levelStart = null, metrics = {}, onClose = null, onRestart }) {
    const inheritedClose = React.useContext(GameCloseContext);
    const inheritedResult = React.useContext(GameResultContext);
    const highScoreResult = React.useContext(BrainGameHighScoreContext);
    const dialogRef = useRef(null);
    const completionReportedRef = useRef(false);
    const close = onClose || inheritedClose;
    const resolvedCopy = copy || inheritedResult?.copy || {};
    const resolvedGame = game || inheritedResult?.game;
    const resolvedHud = hud || inheritedResult?.hud || {};
    const resolvedMetrics = {
        ...(inheritedResult?.metrics || {}),
        ...metrics,
    };
    const resolvedLevelStart = Number.isFinite(levelStart) ? levelStart : inheritedResult?.levelStart;
    const finalLevel = Number.isFinite(resolvedMetrics.level) && resolvedMetrics.level > 0
        ? Math.round(resolvedMetrics.level)
        : null;

    if (Number.isFinite(inheritedResult?.bestStreak) || Number.isFinite(resolvedMetrics.streak)) {
        resolvedMetrics.streak = Math.max(
            Number.isFinite(inheritedResult?.bestStreak) ? inheritedResult.bestStreak : 0,
            Number.isFinite(resolvedMetrics.streak) ? resolvedMetrics.streak : 0,
        );
    }

    const restart = () => {
        inheritedResult?.resetResultRun?.();
        onRestart?.();
    };
    useDialogFocusTrap(dialogRef, null, '.UFOV-brain-games__play button:not([disabled]), .UFOV-brain-games__top-actions .UFOV-brain-games__icon-button:not([disabled])');

    useEffect(() => {
        if (completionReportedRef.current || typeof inheritedResult?.reportRunComplete !== 'function') {
            return;
        }

        completionReportedRef.current = true;
        inheritedResult.reportRunComplete(resolvedMetrics);
    }, [inheritedResult, resolvedMetrics]);

    return (
        <div className={`UFOV-brain-games__result ${className}`.trim()} role="presentation">
            <div
                aria-label={resolvedCopy.runComplete || 'Run complete'}
                aria-modal="true"
                className="UFOV-brain-games__result-dialog"
                ref={dialogRef}
                role="dialog"
                tabIndex="-1"
            >
                <div className="UFOV-brain-games__result-head">
                    <span aria-hidden="true" />
                    {finalLevel ? (
                        <div className="UFOV-brain-games__result-level">
                            {resolvedCopy.currentDifficulty || resolvedCopy.level || 'Difficulty'} {finalLevel}
                        </div>
                    ) : null}
                </div>
                <div className="UFOV-brain-games__result-stage">
                    <div className="UFOV-brain-games__result-summary">
                        <h1>{resolvedCopy.runComplete || 'Run complete'}</h1>
                        {resolvedGame?.title ? <p>{resolvedGame.title}</p> : null}
                    </div>
                    <PersonalBestResult copy={resolvedCopy} highScoreResult={highScoreResult} />
                    <ResultMetrics copy={resolvedCopy} hud={resolvedHud} metrics={resolvedMetrics} />
                    <ResultLevelChange copy={resolvedCopy} level={finalLevel} levelStart={resolvedLevelStart} />
                </div>
                <div className="UFOV-brain-games__result-actions">
                    <button className="is-primary" type="button" onClick={restart}>{resolvedCopy.startAgain || 'Start again'}</button>
                    {close ? <button className="is-secondary" type="button" onClick={close}>{resolvedCopy.close || 'Close'}</button> : null}
                </div>
            </div>
        </div>
    );
}

function TimeUpOverlay({ copy = {}, game = null, hud = {}, levelStart = null, metrics = {}, onClose, onRestart }) {
    return (
        <GameResultOverlay
            className="UFOV-brain-games__time-up"
            copy={copy}
            game={game}
            hud={hud}
            levelStart={levelStart}
            metrics={{ ...metrics, durationMs: BRAIN_GAME_DURATION_MS }}
            onClose={onClose}
            onRestart={onRestart}
        />
    );
}


export {
    GAME_ROUTE_PREFIX,
    MEMORY_CARDS,
    PATH_GRID_COLUMNS,
    PATH_GRID_ROWS,
    PATH_GRID_SIZE,
    SEQUENCE_PAD_COUNT,
    N_BACK_SYMBOLS,
    STROOP_COLORS,
    COLOR_BLIND_STROOP_COLORS,
    VISUAL_SEARCH_SYMBOLS,
    FLANKER_DIRECTIONS,
    FLANKER_ICON_BY_DIRECTION,
    CHANCE_TOTALS,
    GO_NO_GO_TARGETS,
    GO_NO_GO_DISTRACTORS,
    GO_NO_GO_RESPONSE_MS,
    GO_NO_GO_FEEDBACK_MS,
    BRAIN_GAME_DURATION_MS,
    BrainGameHighScoreContext,
    TRAIL_LABELS,
    BOOTSTRAP_ICONS,
    BRAIN_GAME_ICON_BY_ID,
    BRAIN_GAME_ICON_IMAGE_BY_ID,
    BRAIN_GAME_TUTORIALS,
    BRAIN_ASSET_SETS,
    clamp,
    percent,
    shuffle,
    randomItem,
    formatElapsed,
    formatGameSeconds,
    useTicker,
    usePausableTimeout,
    getNextAssetSet,
    createMemoryState,
    createMemoryDeck,
    getPathNeighbors,
    createPathMemoryRound,
    createTrailNodes,
    createNBackSequence,
    createStroopRound,
    createVisualSearchRound,
    createFlankerRound,
    createChanceOption,
    createChanceRound,
    createGoNoGoStimuli,
    createTurnMatchRound,
    createNumberRainRound,
    createOrderRushRound,
    createEstimateDots,
    createEstimateDuelRound,
    createNewCatchRound,
    BrainTrainingGameIcon,
    BrainGameHeader,
    TutorialModal,
    GameShell,
    GameButton,
    GameResultOverlay,
    TimeUpOverlay,
};
